import { useEffect } from 'react'

const defaultMessage =
  'An upload is still in progress. If you leave now it will be cancelled.'

/**
 * Warns the user before they leave the current page while `when` is true (e.g.
 * while an upload is in flight). Covers the three ways a user can leave a page
 * in this single-page app:
 *
 *   1. Closing the tab / refreshing / typing a new URL -> native `beforeunload`
 *      confirmation (browsers show their own standard wording here).
 *   2. Clicking an in-app link -> intercepted in the capture phase before React
 *      Router handles it, so we can cancel the client-side navigation.
 *   3. Pressing the browser back / forward button -> a sentinel history entry
 *      lets us intercept the `popstate` and stay put if the user cancels.
 *
 * The app uses a plain `BrowserRouter` (not a data router), so React Router's
 * `useBlocker` is unavailable; this hook fills that gap without restructuring
 * the router.
 */
export function useNavigationGuard(when: boolean, message = defaultMessage) {
  useEffect(() => {
    if (!when) return

    // 1. Real page unloads (close tab, refresh, external navigation).
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    // 2. In-app link clicks. Intercept in the capture phase so we run before
    //    React Router's delegated click handler; stopping propagation there
    //    prevents the client-side navigation entirely.
    const handleClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }

      // `event.target` is typed as `EventTarget | null` and isn't guaranteed
      // to be an `Element` (e.g. a text node), so calling `.closest()` on it
      // unguarded could throw. Narrow to `Element` first.
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a')
      if (!anchor) return
      if (anchor.target && anchor.target !== '_self') return
      if (anchor.hasAttribute('download')) return

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) return

      // External links trigger a real unload, which `beforeunload` already
      // covers; only guard same-origin (client-side) navigations here.
      const destination = new URL(anchor.href, globalThis.location.href)
      if (destination.origin !== globalThis.location.origin) return

      if (!globalThis.confirm(message)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    // 3. Back / forward button. Push a sentinel entry (same URL, so nothing
    //    visibly changes) so the first `popstate` lands here and we can prompt.
    const handlePopState = () => {
      if (globalThis.confirm(message)) {
        globalThis.removeEventListener('popstate', handlePopState)
        globalThis.history.back()
      } else {
        // Re-arm the sentinel, preserving the existing history state so React
        // Router (and any other `history.state` consumers) keep their shape.
        globalThis.history.pushState(
          globalThis.history.state,
          '',
          globalThis.location.href
        )
      }
    }

    globalThis.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('click', handleClickCapture, true)
    globalThis.history.pushState(
      globalThis.history.state,
      '',
      globalThis.location.href
    )
    globalThis.addEventListener('popstate', handlePopState)

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleClickCapture, true)
      globalThis.removeEventListener('popstate', handlePopState)
    }
  }, [when, message])
}
